import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import { Theme } from "./Theme";
import { useLandingReady, useLandingChat } from "./LandingReadyContext";
import { SUPPORT_URL } from "./Api_url";
import { playChatNotificationSound, prepareChatNotificationSound } from "./chatNotificationSound";
import { useToast } from "./ToastContext";
import { reloadForChatwootInbox } from "./chatwootDocumentNavigation";

const BASE_URL = SUPPORT_URL;
// Public website inbox token supplied by Chatwoot, not an account API key.
const WEBSITE_TOKEN = "8NTMWPPecDNCKPboKttBChsd";
const ACCOUNT_WEBSITE_TOKEN = "A9XUCDRmYZ6cwiugxaoTiqJM";
const SCRIPT_ID = "hydra-chatwoot-sdk";
const CHATWOOT_ORIGIN = BASE_URL ? new URL(BASE_URL).origin : "";
const WIDGET_MESSAGE_PREFIX = "chatwoot-widget:";
const ACCOUNT_UNREAD_TOAST_MESSAGE = "У Вас новое сообщение в Чате поддержки";

declare global {
  interface Window {
    chatwootSettings?: {
      position: "right" | "left";
      type: "standard" | "expanded_bubble";
      launcherTitle: string;
      hideMessageBubble?: boolean;
      showUnreadMessagesDialog?: boolean;
      darkMode?: "dark" | "light" | "auto";
      locale?: string;
    };
    chatwootSDK?: {
      run: (options: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      websiteToken?: string;
      user?: unknown;
      identifier?: string | number;
      resetTriggered?: boolean;
      hasLoaded?: boolean;
      reset?: () => void;
      setUser?: (identifier: string, user: {
        name: string;
        identifier_hash?: string;
        custom_attributes: { user_id: number };
      }) => void;
      isOpen?: boolean;
      toggle: (state: "open" | "close") => void;
      toggleBubbleVisibility: (visibility: "show" | "hide") => void;
    };
  }
}

// Keep this outside the animated outlet so visibility follows the current URL,
// even while the old landing page is still playing its exit animation.
export const LandingChatwoot = () => {
  const { showToast } = useToast();
  const { pathname } = useLocation();
  const isPublicChatRoute = Boolean(BASE_URL) && ["/", "/auth", "/register"].includes(pathname);
  const landingReady = useLandingReady();
  const { accountUser, infoHappUser, chatOpen, setChatReady, setChatOpen, setUnread } = useLandingChat();
  const chatUser = pathname === "/main" ? accountUser : pathname === "/infohapp" ? infoHappUser : null;
  const isAccountChatRoute = Boolean(BASE_URL && chatUser);
  const canShow = (isPublicChatRoute && landingReady) || isAccountChatRoute;
  const token = isAccountChatRoute ? ACCOUNT_WEBSITE_TOKEN : WEBSITE_TOKEN;
  // Use the same identifier across sessions so Chatwoot can find this contact.
  const identityKey = isAccountChatRoute && chatUser
    ? `${chatUser.userId}:${chatUser.identifierHash ?? ""}` : "anonymous";
  const activeIdentity = useRef<string | null>(null);
  const reloadPending = useRef(false);
  const lastNotifiedMessage = useRef<string | null>(null);
  const hasNotifiedUnread = useRef(false);

  const switchInboxDocument = useCallback((previousToken?: string) => {
    if (reloadPending.current) return;
    reloadPending.current = true;
    // Chatwoot's reset() changes iframe.src and adds a browser-history entry.
    // Reload the document to switch inboxes without trapping the Back button.
    setChatReady(false);
    setChatOpen(false);
    setUnread(false);
    window.$chatwoot?.toggle("close");
    window.$chatwoot?.toggleBubbleVisibility("hide");
    reloadForChatwootInbox(previousToken);
  }, [setChatReady, setChatOpen, setUnread]);

  useLayoutEffect(() => {
    // The account user is registered only after the profile screen renders.
    // Switch a public widget before that render is painted, otherwise the
    // account appears briefly and the following reload shows a second loader.
    const enteringAccount = ["/main", "/profile", "/pay", "/install"].includes(pathname);
    const script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (enteringAccount && script?.dataset.initialized === "true" &&
        window.$chatwoot?.websiteToken === WEBSITE_TOKEN) {
      switchInboxDocument(WEBSITE_TOKEN);
    }
  }, [pathname, switchInboxDocument]);

  useEffect(() => {
    hasNotifiedUnread.current = false;
  }, [pathname, identityKey]);

  useEffect(() => {
    window.addEventListener("pointerdown", prepareChatNotificationSound);
    window.addEventListener("click", prepareChatNotificationSound);
    window.addEventListener("keydown", prepareChatNotificationSound);
    window.addEventListener("touchend", prepareChatNotificationSound);
    return () => {
      window.removeEventListener("pointerdown", prepareChatNotificationSound);
      window.removeEventListener("click", prepareChatNotificationSound);
      window.removeEventListener("keydown", prepareChatNotificationSound);
      window.removeEventListener("touchend", prepareChatNotificationSound);
    };
  }, []);

  useEffect(() => {
    if (!isAccountChatRoute || !chatOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") window.$chatwoot?.toggle("close");
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [isAccountChatRoute, chatOpen]);

  useEffect(() => {
    // The widget reports its unread count after loading saved conversations and
    // whenever it changes. Its SDK ignores the dot while the native bubble is
    // hidden, so read the same iframe message for our own launcher indicators.
    const unreadCount = (event: MessageEvent) => {
      if (!(isPublicChatRoute || isAccountChatRoute) || event.origin !== CHATWOOT_ORIGIN) return;
      const iframe = document.getElementById("chatwoot_live_chat_widget") as HTMLIFrameElement | null;
      if (!iframe || event.source !== iframe.contentWindow || typeof event.data !== "string" ||
          !event.data.startsWith(WIDGET_MESSAGE_PREFIX)) return;
      if (isAccountChatRoute && window.$chatwoot?.identifier !== String(chatUser?.userId)) return;

      try {
        const payload = JSON.parse(event.data.slice(WIDGET_MESSAGE_PREFIX.length));
        if (payload.event !== "handleNotificationDot" ||
            !Number.isFinite(payload.unreadMessageCount)) return;
        const hasUnread = payload.unreadMessageCount > 0;
        const chatIsOpen = Boolean(window.$chatwoot?.isOpen);
        setUnread(hasUnread && !chatIsOpen);
        if (!hasUnread) {
          hasNotifiedUnread.current = false;
        } else if (isAccountChatRoute && !chatIsOpen && !hasNotifiedUnread.current) {
          hasNotifiedUnread.current = true;
          showToast({
            type: "success",
            message: ACCOUNT_UNREAD_TOAST_MESSAGE,
          });
        }
      } catch {
        // Other widget messages are handled by Chatwoot's own listener.
      }
    };
    window.addEventListener("message", unreadCount);
    return () => window.removeEventListener("message", unreadCount);
  }, [isPublicChatRoute, isAccountChatRoute, chatUser, setUnread, showToast]);

  useEffect(() => {
    const opened = () => { setChatOpen(true); setUnread(false); };
    const closed = () => {
      setChatOpen(false);
      const trigger = document.getElementById("account-email-support") || document.getElementById("landing-support-button");
      if (window.matchMedia?.("(pointer: coarse)").matches) {
        if (document.activeElement === trigger) trigger?.blur();
      } else {
        trigger?.focus({ preventScroll: true });
      }
    };
    const message = (event: Event) => {
      if (!canShow) return;
      const detail = (event as CustomEvent).detail;
      const receivedMessage = detail?.message ?? detail;
      if (receivedMessage?.message_type !== 1 && receivedMessage?.message_type !== "outgoing") return;
      if (chatOpen || window.$chatwoot?.isOpen) return;
      const messageId = receivedMessage?.id;
      const notificationKey = messageId == null ? null : `${token}:${messageId}`;
      if (notificationKey && lastNotifiedMessage.current === notificationKey) return;
      lastNotifiedMessage.current = notificationKey;
      setUnread(true);
      playChatNotificationSound();
      if (isAccountChatRoute) {
        hasNotifiedUnread.current = true;
        showToast({
          type: "success",
          message: ACCOUNT_UNREAD_TOAST_MESSAGE,
        });
      }
    };
    const error = (event: Event) => {
      if (isAccountChatRoute) console.warn("Chatwoot отклонил идентификацию пользователя:", (event as CustomEvent).detail);
    };
    window.addEventListener("chatwoot:opened", opened);
    window.addEventListener("chatwoot:closed", closed);
    window.addEventListener("chatwoot:on-message", message);
    window.addEventListener("chatwoot:error", error);
    return () => {
      window.removeEventListener("chatwoot:opened", opened);
      window.removeEventListener("chatwoot:closed", closed);
      window.removeEventListener("chatwoot:on-message", message);
      window.removeEventListener("chatwoot:error", error);
    };
  }, [canShow, chatOpen, isAccountChatRoute, token, setChatOpen, setUnread, showToast]);

  useEffect(() => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    let disposed = false;

    const hide = () => {
      if (script?.dataset.ready !== "true") return;
      window.$chatwoot?.toggle("close");
      window.$chatwoot?.toggleBubbleVisibility("hide");
    };

    const syncVisibility = () => {
      if (disposed || script?.dataset.ready !== "true") return;
      setChatReady(canShow);
      window.$chatwoot?.toggleBubbleVisibility("hide");
      if (!canShow) hide();
    };

    const start = () => {
      if (disposed || reloadPending.current || !(isPublicChatRoute || isAccountChatRoute) || !window.chatwootSDK || !script) return;

      if (script.dataset.initialized !== "true") {
        // Start hidden: the user may leave before chatwoot:ready arrives.
        window.chatwootSettings = {
          position: "right",
          type: "standard",
          launcherTitle: "Поддержка",
          hideMessageBubble: true,
          showUnreadMessagesDialog: false,
          darkMode: "dark",
          locale: "ru",
        };
        window.chatwootSDK.run({ websiteToken: token, baseUrl: BASE_URL });
        script.dataset.initialized = "true";
        activeIdentity.current = identityKey;
      } else if (activeIdentity.current !== identityKey || (window.$chatwoot?.websiteToken && window.$chatwoot.websiteToken !== token)) {
        switchInboxDocument(window.$chatwoot?.websiteToken);
        return;
      }
      syncVisibility();
    };

    const handleReady = () => {
      if (disposed) return;
      if (isAccountChatRoute && chatUser) {
        const sdk = window.$chatwoot;
        if (!sdk?.setUser) return;
        const identifier = String(chatUser.userId);
        // Chatwoot caches setUser by inbox cookie. After a full reload the SDK
        // has no identifier in memory, so bypass that cache and identify again.
        if (sdk.identifier !== identifier) {
          document.cookie = `cw_user_${ACCOUNT_WEBSITE_TOKEN}=; Max-Age=0; Path=/; SameSite=Lax`;
        }
        // Derive all Chatwoot fields from the ID so no login or email is sent.
        sdk.setUser(identifier, {
          name: identifier,
          ...(chatUser.identifierHash ? { identifier_hash: chatUser.identifierHash } : {}),
          custom_attributes: { user_id: chatUser.userId },
        });
      }
      if (script) script.dataset.ready = "true";
      syncVisibility();
    };

    window.addEventListener("chatwoot:ready", handleReady);
    if (isPublicChatRoute || isAccountChatRoute) {
      const isNewScript = !script;
      if (!script) {
        script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = `${BASE_URL}/packs/js/sdk.js`;
        script.async = true;
        const createdScript = script;
        // Keep failure cleanup active even if navigation happens during loading.
        script.addEventListener("error", () => {
          createdScript.remove();
          console.warn("Не удалось загрузить чат поддержки Chatwoot.");
        }, { once: true });
      }
      script.addEventListener("load", start);
      if (window.chatwootSDK) start();
      if (isNewScript) document.head.appendChild(script);
    } else {
      hide();
      // Keep the iframe across internal account pages. A new inbox is loaded
      // with a fresh document when the user enters a public chat route.
    }

    return () => {
      disposed = true;
      setChatReady(false);
      setChatOpen(false);
      window.removeEventListener("chatwoot:ready", handleReady);
      script?.removeEventListener("load", start);
      hide();
    };
  }, [pathname, isPublicChatRoute, isAccountChatRoute, chatUser, token, identityKey, canShow, setChatReady, setChatOpen, switchInboxDocument]);

  return <ChatwootStyles $visible={canShow} />;
};

const ChatwootStyles = createGlobalStyle<{ $visible: boolean }>`
  /* Also guard against SDK messages reopening the widget during navigation. */
  ${({ $visible }) => !$visible && `
    .woot-widget-holder, .woot--bubble-holder, .woot-widget-bubble,
    .woot--close, .woot--notification {
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `}

  /* The header is the only launcher; keep SDK bubbles hidden in every state. */
  .woot--bubble-holder, .woot-widget-bubble { display: none !important; }
  @media (min-width: 668px) {
    body .woot-widget-holder:not(.woot--hide) {
      top: 90px !important;
      bottom: auto !important;
      height: min(640px, calc(100dvh - 110px)) !important;
      min-height: 0 !important;
    }
  }
  .woot-widget-holder {
    border: 1px solid ${Theme.colors.borderColor};
    border-radius: ${Theme.spacing.radiusLg} !important;
    background: ${Theme.colors.blockColor};
    box-shadow: 0 18px 60px rgba(0, 0, 0, 0.5) !important;
    overflow: hidden;
  }
  @media (max-width: 667px) {
    .woot-widget-holder { border-radius: 0 !important; border: 0; }
  }
`;
